import { useEffect } from 'react';
import {
  isNudgytAgentCoreSession,
  isNudgytAgentSession,
  postMessage
} from '../helpers/utils';
import { normalizeStructuredUserEmotion } from '../helpers/userEmotion';
import { useSimulationContext } from '../providers/SimulationProvider';
import {
  MESSAGE_FROM,
  MESSAGE_TYPES,
  NUDGYT_AGENT_MESSAGE_TYPES,
  SIMULATION_TYPES
} from '../constants/simulationConstants';

// Parent-sent payloads that control an in-flight reply rather than start one.
const CONTROL_PAYLOAD_TYPES = [
  NUDGYT_AGENT_MESSAGE_TYPES.STOP_RESPONSE,
  NUDGYT_AGENT_MESSAGE_TYPES.STOP_AUDIO
];

function useChatMessageListener() {
  // Global State (Context API)
  const {
    isClientInitialized,
    simulationType,
    characterEngineType,
    setSelectedCharacterId,
    setUserText,
    setDocumentIds,
    setUserInteract,
    setIsStopResponseRequested,
    setTextQueryUserEmotion
  } = useSimulationContext();

  // Routes user input to the agent handler instead of the Convai one. A Core
  // character reports simulationType BASIC but converses through the agent.
  const isNudgytAgentChat = isNudgytAgentSession({
    simulationType,
    characterEngineType
  });

  const handleCoachingSimulation = (receivedDataPayload) => {
    if (!simulationType || !isNudgytAgentChat) return;
    // Core Basic uses handleCoreBasicSimulation instead.
    if (
      isNudgytAgentCoreSession({ characterEngineType }) &&
      simulationType === SIMULATION_TYPES.BASIC
    )
      return;
    if (
      receivedDataPayload?.type === NUDGYT_AGENT_MESSAGE_TYPES.STOP_RESPONSE
    ) {
      setIsStopResponseRequested(true);
      return;
    }
    // Any other control payload (stop_audio) is handled by the agent hooks'
    // own listeners; falling through would echo an empty user turn back to the
    // parent and put a blank message in the transcript.
    if (CONTROL_PAYLOAD_TYPES.includes(receivedDataPayload?.type)) return;

    if (receivedDataPayload?.type === NUDGYT_AGENT_MESSAGE_TYPES.USER_INTERACT)
      setUserInteract(receivedDataPayload);

    const hasMessage =
      typeof receivedDataPayload?.message === 'string' &&
      receivedDataPayload.message.trim();
    const hasFiles =
      Array.isArray(receivedDataPayload?.fileIds) &&
      receivedDataPayload.fileIds.length > 0;

    if (
      typeof receivedDataPayload.characterId === 'string' &&
      (hasMessage || hasFiles)
    ) {
      setSelectedCharacterId(receivedDataPayload.characterId);
      if (hasMessage) setUserText(receivedDataPayload.message);
      // Set document IDs if provided (Coaching Simulation)
      if (hasFiles) setDocumentIds(receivedDataPayload.fileIds);
    }

    const isAiSuggestion =
      receivedDataPayload.type === NUDGYT_AGENT_MESSAGE_TYPES.AI_SUGGESTION;
    const aiSuggestionLabel = receivedDataPayload.label;
    const defaultMessage = receivedDataPayload.message;
    const payload = {
      from: MESSAGE_FROM.USER,
      message: isAiSuggestion ? aiSuggestionLabel : defaultMessage
    };
    postMessage({ type: MESSAGE_TYPES.CHAT, payload });
  };

  const handleCoreBasicSimulation = (receivedDataPayload) => {
    if (!isNudgytAgentCoreSession({ characterEngineType })) return;
    if (simulationType !== SIMULATION_TYPES.BASIC) return;

    if (
      receivedDataPayload?.type === NUDGYT_AGENT_MESSAGE_TYPES.STOP_RESPONSE
    ) {
      setIsStopResponseRequested(true);
      return;
    }

    // Any other control payload (stop_audio) is handled by the agent hooks'
    // own listeners; it is not a user turn.
    if (CONTROL_PAYLOAD_TYPES.includes(receivedDataPayload?.type)) return;

    if (
      receivedDataPayload?.type === NUDGYT_AGENT_MESSAGE_TYPES.USER_INTERACT
    ) {
      setUserInteract(receivedDataPayload);
      return;
    }

    const hasMessage =
      typeof receivedDataPayload?.message === 'string' &&
      receivedDataPayload.message.trim();

    if (!hasMessage) return;

    if (typeof receivedDataPayload.characterId === 'string') {
      setSelectedCharacterId(receivedDataPayload.characterId);
    }

    const structuredUserEmotion = normalizeStructuredUserEmotion(
      receivedDataPayload.userEmotionCore
    );

    setUserText(receivedDataPayload.message);
    setTextQueryUserEmotion(structuredUserEmotion);
  };

  const handleBasicSimulation = (receivedDataPayload) => {
    if (simulationType !== SIMULATION_TYPES.BASIC || isNudgytAgentChat) return;
    // Control messages are not user turns — the Convai hooks listen for these
    // themselves. Falling through would clear the selected character and the
    // pending user text on every Stop.
    if (CONTROL_PAYLOAD_TYPES.includes(receivedDataPayload?.type)) return;

    const hasMessage =
      typeof receivedDataPayload?.message === 'string' &&
      receivedDataPayload.message.trim();

    if (typeof receivedDataPayload.characterId !== 'string' || !hasMessage)
      console.warn('Invalid message format:', receivedDataPayload);

    setSelectedCharacterId(receivedDataPayload.characterId);
    setUserText(receivedDataPayload.message);
  };

  // Listen for messages from the parent application
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        let receivedData = event.data;

        if (typeof receivedData === 'string') {
          try {
            receivedData = JSON.parse(receivedData);
          } catch (err) {
            return;
          }
        }
        const receivedDataPayload = receivedData.payload;

        if (receivedData.type !== MESSAGE_TYPES.CHAT || !isClientInitialized)
          return;

        handleCoreBasicSimulation(receivedDataPayload);
        handleCoachingSimulation(receivedDataPayload);
        handleBasicSimulation(receivedDataPayload);
      } catch (error) {
        console.error('[Chat Message Listener Error]', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isClientInitialized,
    simulationType,
    characterEngineType,
    setSelectedCharacterId,
    setUserText,
    setDocumentIds,
    setUserInteract,
    setIsStopResponseRequested,
    setTextQueryUserEmotion
  ]);
}

export default useChatMessageListener;
